import { useMsal } from '@azure/msal-react';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, makeStyles, mergeClasses, Option } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useCommonStyles, useStores } from '../../../common/hooks';
import { Reviewer } from '../../../models';
import { SelectOptions } from '../../shared-components/form';
import { LoadingSpinner } from '../../shared-components/LoadingSpinner';
import { NominationForStaffRouteParams } from '../../../models/route-params';

interface AssignNominationReviewerModalProps {
  isModalOpen: boolean;
  currentReviewer?: Reviewer;
  onDismissModal: () => void;
}

const useStyles = makeStyles({
  surface: { maxWidth: '416px' }
});

const AssignNominationReviewerModalImpl: React.FC<AssignNominationReviewerModalProps> = (props) => {
  const {
    isModalOpen,
    currentReviewer,
    onDismissModal,
  } = props;

  const {
    instance,
  } = useMsal();

  const {
    apiStore,
    awardStore,
    nominationForStaffPageUiStore,
  } = useStores();

  const commonStyles = useCommonStyles();
  const styles = useStyles();

  const {
    nominationId: nominationIdValue,
    countryId: countryIdValue
  } = useParams<NominationForStaffRouteParams>();

  const nominationId = nominationIdValue ? Number(nominationIdValue) : undefined;
  const countryId = countryIdValue ? Number(countryIdValue) : undefined;

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getAvailableNominationReviewers = async (): Promise<void> => {
    if (!awardStore.defaultProgramRef || !nominationId) return;

    let availableReviewers: Reviewer[];
    if (nominationForStaffPageUiStore.isCountryAward) {
      if (countryId === undefined) return;

      availableReviewers = await apiStore.getAvailableProgramCountryNominationReviewers(
        instance,
        awardStore.defaultProgramRef.programId,
        countryId,
      );
    } else {
      availableReviewers = await apiStore.getAvailableNominationReviewers(instance, nominationId);
    }

    nominationForStaffPageUiStore.setAvailableNominationReviewers(availableReviewers);
  }

  const getAssignedNominationReviewer = async (nominationId: number): Promise<void> => {
    if (!awardStore.defaultProgramRef) return;

    let assignedReviewer: Reviewer | undefined;
    if (nominationForStaffPageUiStore.isCountryAward) {
      if (countryId === undefined) return;

      assignedReviewer = await apiStore.getAssignedCountryNominationReviewer(
        instance,
        awardStore.defaultProgramRef.programId,
        countryId,
        nominationId,
      );
    } else {
      assignedReviewer = await apiStore.getAssignedNominationReviewer(instance, nominationId);
    }

    nominationForStaffPageUiStore.setCurrentReviewer(assignedReviewer);
  }

  const upsertNominationReviewer = async (): Promise<void> => {
    if (!awardStore.defaultProgramRef || !nominationId || !nominationForStaffPageUiStore.reviewerData) return;

    if (nominationForStaffPageUiStore.isCountryAward) {
      if (countryId === undefined) return;

      await apiStore.upsertCountryNominationReviewer(
        instance,
        awardStore.defaultProgramRef.programId,
        countryId,
        nominationId,
        nominationForStaffPageUiStore.reviewerData,
      );
    } else {
      await apiStore.upsertNominationReviewer(
        instance,
        nominationId,
        nominationForStaffPageUiStore.reviewerData,
      );
    }
  }

  const handleSave = async (): Promise<void> => {
    const hasReviewerContext = awardStore.defaultProgramRef !== undefined
      && (!nominationForStaffPageUiStore.isCountryAward || countryId !== undefined);
    if (!hasReviewerContext || !nominationForStaffPageUiStore.reviewerData || !nominationId) return;

    setIsLoading(true);
    try {
      await upsertNominationReviewer();
      await getAssignedNominationReviewer(nominationId);
      onDismissModal();
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  }

  const preloadData = async (): Promise<void> => {
    setIsLoading(true);
    try {
      if (!nominationId) return;

      if (currentReviewer) {
        nominationForStaffPageUiStore.setSelectedReviewerEmailAddress(currentReviewer.emailAddress);
      }

      await getAvailableNominationReviewers();
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  }

  const hasReviewerContext = awardStore.defaultProgramRef !== undefined
    && (!nominationForStaffPageUiStore.isCountryAward || countryId !== undefined);
  const isSaveDisabled = !hasReviewerContext
    || !nominationForStaffPageUiStore.reviewerData
    || isLoading
    || nominationId === 0
    || nominationForStaffPageUiStore.isSelectedReviewer(currentReviewer);

  const handleReviewerSelect = (optionValue?: string): void => {
    nominationForStaffPageUiStore.selectNominationReviewer(optionValue ?? '');
  }

  useEffect(() => {
    if (isModalOpen) {
      preloadData();
    }
    return () => {
      nominationForStaffPageUiStore.resetNominationReviewerModal();
    };
  }, [isModalOpen]);

  return (
    <Dialog
      open={isModalOpen}
      onOpenChange={(_, data) => {
        if (!data.open) onDismissModal();
      }}
    >
      <DialogSurface className={mergeClasses('assign-nomination-reviewer-modal', commonStyles.modalSurface, styles.surface)}>
        <DialogBody className={commonStyles.modalBody}>
          <DialogTitle
            action={<Button
              appearance='transparent'
              title='Close'
              icon={<DismissRegular />}
              onClick={onDismissModal}
            />}
          >
            Assign a reviewer
          </DialogTitle>
          <DialogContent>
            {
              isLoading ? <LoadingSpinner /> : (
                <SelectOptions
                  label='Select a reviewer'
                  onOptionSelect={(data) => handleReviewerSelect(data.optionValue)}
                  dropdownProps={{ value: nominationForStaffPageUiStore.selectedReviewerEmailAddress ?? '' }}
                >
                  {
                    nominationForStaffPageUiStore.availableNominationReviewers.map((reviewer) => (
                      <Option
                        key={reviewer.emailAddress}
                        value={reviewer.emailAddress}
                      >
                        {reviewer.fullName ? reviewer.fullName : reviewer.emailAddress}
                      </Option>
                    ))
                  }
                </SelectOptions>
              )
            }
          </DialogContent>
          <DialogActions>
            <Button
              appearance='secondary'
              onClick={() => onDismissModal()}
            >
              Cancel
            </Button>
            <Button
              appearance='primary'
              disabled={isSaveDisabled}
              onClick={() => handleSave()}
            >
              Save
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export const AssignNominationReviewerModal = observer(AssignNominationReviewerModalImpl);
